/**
 * v3.4.2 — SingleImportContext
 * =============================================================================
 * Autosaves the single-file import in-progress state so beta users don't
 * lose their per-row categorisations by navigating away.
 *
 * Bug that triggered this: Diya categorised ~20 rows in the preview stage,
 * navigated to /transactions to double-check something, came back to
 * /import — categories were gone. Root cause: previewRows lived only in
 * CSVImport.js component state, which unmounts on route change.
 *
 * Design
 * ------
 * • Lift the *persistent* pieces of preview state (rows + user picks) into
 *   an App-level context so React Router navigation doesn't drop them.
 * • Mirror those pieces to sessionStorage keyed by import_id so a hard
 *   browser refresh survives too — until the tab is closed.
 * • Everything transient (file dropzone refs, dialogs, in-flight upload
 *   progress) stays local to CSVImport.js — no benefit to lifting those.
 *
 * NOT covered on purpose:
 * • Cross-device / cross-tab persistence — that's the domain of a
 *   backend PATCH endpoint and belongs in a later phase if this fix
 *   proves insufficient.
 * • File-object persistence — File is not JSON-serialisable and gets
 *   invalidated by the browser after the tab closes anyway. If a hard
 *   refresh happens BEFORE the file was uploaded to /api/import/csv/stage
 *   (i.e. we don't have _import_id yet), we discard the state on rehydrate
 *   — nothing to resume.
 *
 * Keyed by import_id so a user re-uploading a DIFFERENT statement doesn't
 * pick up someone else's stale rows. When the commit completes or the
 * user explicitly resets, we clear both context AND sessionStorage.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'twenty120.singleImport.v1';

// Only the fields that need to survive nav/refresh. Everything else stays
// in CSVImport.js local useState.
const PERSISTED_FIELDS = ['previewRows', 'sourceType', 'fileName', 'accountId', 'stage', 'lastEditedAt'];

function readFromSessionStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Basic sanity: only rehydrate if we have staged rows with an import id.
    // Anything else is either a fresh page (nothing to restore) or a state
    // from before /api/import/csv/stage completed (file is gone, useless).
    const rows = Array.isArray(parsed?.previewRows) ? parsed.previewRows : [];
    const hasStagedRows = rows.some((r) => r?._import_id && r?._authority_mode === 'commit_v1_stage');
    if (!hasStagedRows) return null;
    return parsed;
  } catch (_e) {
    return null;
  }
}

function writeToSessionStorage(state) {
  try {
    const rows = Array.isArray(state.previewRows) ? state.previewRows : [];
    const hasStagedRows = rows.some((r) => r?._import_id && r?._authority_mode === 'commit_v1_stage');
    if (!hasStagedRows) {
      // Nothing worth persisting yet — clear any stale entry.
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    const payload = {};
    PERSISTED_FIELDS.forEach((k) => { payload[k] = state[k]; });
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (_e) {
    // sessionStorage full or blocked (private mode) — silently drop, this
    // is best-effort backup, in-memory context still works.
  }
}

const SingleImportContext = createContext(null);

export function SingleImportProvider({ children }) {
  // Rehydrate on mount from sessionStorage.
  const initial = useRef(readFromSessionStorage()).current;

  const [previewRows, setPreviewRowsRaw] = useState(initial?.previewRows || []);
  const [sourceType, setSourceType] = useState(initial?.sourceType || 'csv');
  const [fileName, setFileName] = useState(initial?.fileName || '');
  const [accountId, setAccountId] = useState(initial?.accountId || null);
  const [stage, setStage] = useState(initial?.stage || 'upload');
  // v3.4.3 — bumped every time previewRows changes so the shell resume
  // pill can show "unfinished import from 12m ago" without hand-wiring
  // a separate ping in every setPreviewRows call site.
  const [lastEditedAt, setLastEditedAt] = useState(initial?.lastEditedAt || null);

  // Wrap setPreviewRows so any edit updates lastEditedAt in the same
  // React batch. Callers keep the useState-style API.
  const setPreviewRows = useCallback((next) => {
    setPreviewRowsRaw(next);
    setLastEditedAt(Date.now());
  }, []);

  // Mirror to sessionStorage whenever any persisted field changes.
  useEffect(() => {
    writeToSessionStorage({ previewRows, sourceType, fileName, accountId, stage, lastEditedAt });
  }, [previewRows, sourceType, fileName, accountId, stage, lastEditedAt]);

  const resetSingleImport = useCallback(() => {
    setPreviewRowsRaw([]);
    setSourceType('csv');
    setFileName('');
    setAccountId(null);
    setStage('upload');
    setLastEditedAt(null);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (_e) { /* no-op */ }
  }, []);

  // Derived: is there a stashed import waiting for the user to finish?
  const hasResumableImport = previewRows.some(
    (r) => r?._import_id && r?._authority_mode === 'commit_v1_stage',
  );

  const value = {
    previewRows, setPreviewRows,
    sourceType, setSourceType,
    fileName, setFileName,
    accountId, setAccountId,
    stage, setStage,
    lastEditedAt,
    resetSingleImport,
    hasResumableImport,
  };

  return (
    <SingleImportContext.Provider value={value}>
      {children}
    </SingleImportContext.Provider>
  );
}

export function useSingleImport() {
  const ctx = useContext(SingleImportContext);
  if (!ctx) {
    throw new Error('useSingleImport must be used inside a SingleImportProvider.');
  }
  return ctx;
}
